import { apiClient } from "./client";

export type ThreadV4ViewCategory = "standard" | "custom" | "agent" | "personal";
export type ThreadV4ViewVisibility = "project" | "private" | "public_link";
export type ThreadV4NotebookEntryType = "note" | "agent" | "system" | "decision" | "task";
export type ThreadV4NotebookBodyFormat = "markdown" | "plain";
export type ThreadV4SpoolSourceType =
  | "manual"
  | "agent"
  | "thread"
  | "slack"
  | "gmail"
  | "drive"
  | "obsidian"
  | "notion"
  | "hubspot"
  | "flow360"
  | "github"
  | "teams"
  | "outlook";
export type ThreadV4SpoolVisibility = "personal" | "work_private" | "work_shared" | "public";
export type ThreadV4WidgetDock = "canvas" | "left" | "right" | "bottom" | "floating";
export type ThreadV4WidgetKind =
  | "ask-chart"
  | "asset-rail"
  | "build-surface"
  | "coverage-map"
  | "dag"
  | "flowfield"
  | "geometry"
  | "markdown-note"
  | "hq-margins"
  | "mesh"
  | "notebook"
  | "plot"
  | "plot-recall"
  | "table";

export interface ThreadV4WidgetPosition {
  x: number;
  y: number;
  w: number;
  h: number;
  dock?: ThreadV4WidgetDock;
}

export interface ThreadV4WidgetSpec {
  id: string;
  kind: ThreadV4WidgetKind;
  title: string;
  position: ThreadV4WidgetPosition;
  bindings?: Record<string, unknown>;
  state?: Record<string, unknown>;
}

export interface ThreadV4LayoutJson {
  schema_version: "thread.view.layout.v1" | string;
  mode: "dashboard" | "freeform" | string;
  cols: number;
  row_height?: number;
  widgets: ThreadV4WidgetSpec[];
}

export interface ThreadV4ViewSpec {
  id: string;
  project_id: string;
  branch: string;
  name: string;
  slug: string;
  category: ThreadV4ViewCategory;
  visibility: ThreadV4ViewVisibility;
  owner_id: string;
  layout_json: ThreadV4LayoutJson;
  widget_state_json: Record<string, unknown>;
  binding_json: Record<string, unknown>;
  metadata: Record<string, unknown>;
  thumbnail_asset_id?: string | null;
  is_default: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface ThreadV4ViewsResponse {
  schema_version: "thread.v4.views.v1";
  project_id: string;
  branch: string;
  views: ThreadV4ViewSpec[];
}

export interface ThreadV4ViewCreate {
  project_id: string;
  branch?: string;
  name: string;
  slug?: string;
  category?: ThreadV4ViewCategory;
  visibility?: ThreadV4ViewVisibility;
  layout_json: ThreadV4LayoutJson;
  widget_state_json?: Record<string, unknown>;
  binding_json?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  thumbnail_asset_id?: string | null;
  is_default?: boolean;
}

export interface ThreadV4AgentViewCreate extends ThreadV4ViewCreate {
  agent_id?: string;
  agent_label?: string;
  prompt?: string;
}

export interface ThreadV4NotebookEntry {
  id: string;
  project_id: string;
  view_id?: string | null;
  branch: string;
  entry_type: ThreadV4NotebookEntryType;
  author_id: string;
  author_label?: string | null;
  owner_user_id: string;
  visibility: ThreadV4SpoolVisibility;
  source_type: ThreadV4SpoolSourceType;
  source_uri?: string | null;
  source_external_id?: string | null;
  source_created_at?: string | null;
  captured_at?: string | null;
  body: string;
  body_format: ThreadV4NotebookBodyFormat;
  anchors: Record<string, unknown>;
  source_metadata: Record<string, unknown>;
  metadata: Record<string, unknown>;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface ThreadV4NotebookResponse {
  schema_version: "thread.v4.notebook.v1";
  project_id: string;
  branch: string;
  entries: ThreadV4NotebookEntry[];
}

export interface ThreadV4NotebookCreate {
  project_id: string;
  view_id?: string | null;
  branch?: string;
  entry_type?: ThreadV4NotebookEntryType;
  author_label?: string;
  visibility?: ThreadV4SpoolVisibility;
  source_type?: ThreadV4SpoolSourceType;
  source_uri?: string | null;
  source_external_id?: string | null;
  source_created_at?: string | null;
  body: string;
  body_format?: ThreadV4NotebookBodyFormat;
  anchors?: Record<string, unknown>;
  source_metadata?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

export function fetchThreadV4Views(projectId: string, branch = "main"): Promise<ThreadV4ViewsResponse> {
  const params = new URLSearchParams({ project_id: projectId, branch });
  return apiClient<ThreadV4ViewsResponse>(`/v4/views?${params.toString()}`);
}

export function createThreadV4View(payload: ThreadV4ViewCreate): Promise<ThreadV4ViewSpec> {
  return apiClient<ThreadV4ViewSpec>("/v4/views", {
    method: "POST",
    body: payload,
  });
}

export function createThreadV4AgentView(payload: ThreadV4AgentViewCreate): Promise<ThreadV4ViewSpec> {
  return apiClient<ThreadV4ViewSpec>("/v4/views/from-agent", {
    method: "POST",
    body: payload,
  });
}

export function updateThreadV4View(viewId: string, payload: Partial<ThreadV4ViewCreate>): Promise<ThreadV4ViewSpec> {
  return apiClient<ThreadV4ViewSpec>(`/v4/views/${viewId}`, {
    method: "PATCH",
    body: payload,
  });
}

export function fetchThreadV4Notebook(
  projectId: string,
  branch = "main",
  viewId?: string | null,
): Promise<ThreadV4NotebookResponse> {
  const params = new URLSearchParams({ project_id: projectId, branch });
  if (viewId) params.set("view_id", viewId);
  return apiClient<ThreadV4NotebookResponse>(`/v4/notebook?${params.toString()}`);
}

export function createThreadV4NotebookEntry(payload: ThreadV4NotebookCreate): Promise<ThreadV4NotebookEntry> {
  return apiClient<ThreadV4NotebookEntry>("/v4/notebook", {
    method: "POST",
    body: payload,
  });
}
